import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Key, Clock, CheckCircle, XCircle } from 'lucide-react';
import { apiService } from '../services/api';
import { useNotificationStore } from '../store';
import CreateAPIKeyModal from '../components/APIKeys/CreateAPIKeyModal';

interface APIKey {
  id: string;
  key_prefix: string;
  name: string;
  created_by: number | null;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

const APIKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { addNotification } = useNotificationStore();

  const fetchAPIKeys = useCallback(async () => {
    try {
      setLoading(true);
      const keys = await apiService.getAPIKeys();
      setApiKeys(keys);
    } catch (error: any) {
      addNotification('Failed to load API keys', 'error');
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchAPIKeys();
  }, [fetchAPIKeys]);

  const handleCreateKey = async (name: string) => {
    try {
      const newKey = await apiService.createAPIKey({ name });
      addNotification('API key created successfully', 'success');
      
      // Show the key in a modal (one-time display)
      showKeyModal(newKey.api_key, name);
      
      // Refresh the list
      await fetchAPIKeys();
      setIsCreateModalOpen(false);
    } catch (error: any) {
      addNotification(error.message || 'Failed to create API key', 'error');
    }
  };

  const showKeyModal = (apiKey: string, name: string) => {
    // Create a custom modal to display the key
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
    modal.innerHTML = `
      <div class="bg-gray-800 rounded-lg p-6 max-w-2xl w-full border border-gray-700">
        <h3 class="text-xl font-semibold text-white mb-4">⚠️ API Key Created: ${name}</h3>
        <p class="text-gray-300 mb-4">
          This is the only time you will see this API key. Copy it now and store it securely.
        </p>
        <div class="bg-gray-900 p-4 rounded border border-gray-700 mb-4 flex items-center justify-between">
          <code class="text-green-400 text-sm break-all flex-1">${apiKey}</code>
          <button id="copy-key-btn" class="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
        </div>
        <button id="close-key-modal-btn" class="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded">
          I've Saved the Key
        </button>
      </div>
    `;
    document.body.appendChild(modal);

    // Copy button handler
    modal.querySelector('#copy-key-btn')?.addEventListener('click', () => {
      navigator.clipboard.writeText(apiKey);
      addNotification('API key copied to clipboard', 'success');
    });

    // Close button handler
    modal.querySelector('#close-key-modal-btn')?.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
  };

  const handleRevokeKey = async (keyId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.revokeAPIKey(keyId);
      addNotification('API key revoked successfully', 'success');
      await fetchAPIKeys();
    } catch (error: any) {
      addNotification(error.message || 'Failed to revoke API key', 'error');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading API keys...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">API Keys</h1>
            <p className="text-gray-400 mt-2">
              Manage API keys for external A2A agent authentication
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create API Key
          </button>
        </div>

        {/* API Keys Table */}
        {apiKeys.length === 0 ? (
          <div className="bg-gray-900 rounded-lg p-12 text-center border border-gray-800">
            <Key className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No API Keys Yet</h3>
            <p className="text-gray-400 mb-6">
              Create your first API key to allow external agents to access your A2A pipelines
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              Create API Key
            </button>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Key Prefix
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Key className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-white">{key.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded">
                        {key.key_prefix}...
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {key.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 rounded-full text-xs font-medium">
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 rounded-full text-xs font-medium">
                          <XCircle className="w-4 h-4" />
                          Revoked
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(key.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(key.last_used_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {key.is_active && (
                        <button
                          onClick={() => handleRevokeKey(key.id, key.name)}
                          className="text-red-400 hover:text-red-300"
                          title="Revoke Key"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-400 mb-2">ℹ️ About API Keys</h4>
          <ul className="text-sm text-gray-300 space-y-1">
            <li>• API keys are used to authenticate external agents accessing your A2A pipelines</li>
            <li>• Keys are only shown once at creation - store them securely</li>
            <li>• Revoked keys cannot be reactivated - create a new key if needed</li>
            <li>• Only administrators can create and manage API keys</li>
          </ul>
        </div>
      </div>

      {/* Create API Key Modal */}
      <CreateAPIKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateKey}
      />
    </div>
  );
};

export default APIKeys;

